import React, { FC, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "context/appContext";
import { getMenuItems } from "data/ClientMenu";
import { ProfilClient } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import useIsMobile from "hooks/isMobile";
import { getUserStats } from "redux/features/Admin/Stripe/stripeSlice";
import { ChevronLeft, ChevronRight, Menu as MenuIcon, X, User, Settings } from "lucide-react";
import styles from "./CommonLayout.module.css";

export interface CommonLayoutProps {
  children?: React.ReactNode;
  clientName?: string;
  id?: string;
}

const CommonLayout: FC<CommonLayoutProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const customer_id = authContext?.user?.customer_id;
  const is_customer = authContext?.user?.isCustomer;
  let profile = authContext.user?.profil as ProfilClient;

  const stats = useAppSelector((state) => state?.stripe?.ownerStats);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      getUserStats({
        userId: profile?.id,
        account_id: profile?.account_id || "",
        status: profile?.validation || "",
      })
    );
  }, []);

  const location = useLocation();
  const isActive = location.pathname;
  const isActivePath = (path: string) => location.pathname === path;

  const menuItems = getMenuItems(profile, stats, isActive, customer_id || "", is_customer);
  const isMobile = useIsMobile();

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 40,
      },
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 40,
      },
    },
  };

  const overlayVariants = {
    open: {
      opacity: 1,
      pointerEvents: "auto" as any,
    },
    closed: {
      opacity: 0,
      pointerEvents: "none" as any,
      transition: {
        delay: 0.2,
      },
    },
  };
  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile Menu Button */}
      {isMobile && (
        <div className="fixed top-4 left-4 z-50">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-3 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <AnimatePresence mode="wait">
              {isSidebarOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <MenuIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobile && (
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              variants={overlayVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
        </AnimatePresence>
      )}

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={isMobile ? (isSidebarOpen ? "open" : "closed") : "open"}
        variants={isMobile ? sidebarVariants : {}}
        className={`
          ${isMobile ? "fixed" : "sticky"} top-0 left-0 z-50 
          ${isMobile ? "w-80" : isSidebarCollapsed ? "w-20" : "w-80"} 
          min-h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 
          shadow-xl transition-all duration-300 ease-in-out overflow-hidden
        `}
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {!isSidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{profile?.name || "Client"}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                    {profile?.validation || "En attente"}
                  </p>
                </div>
              </motion.div>
            )}

            {!isMobile && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {isSidebarCollapsed ? (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronLeft className="w-5 h-5 text-gray-500" />
                )}
              </motion.button>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className={`p-4 h-[calc(100vh-140px)] overflow-y-auto ${styles.customScrollbar}`}>
          <div className="space-y-2">
            {menuItems.map((item, index) => {
              if (!item.condition) return null;

              const isActiveItem = isActivePath(item.to);

              return (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to={item.to}
                    onClick={() => isMobile && setIsSidebarOpen(false)}
                    className={`
                      group flex items-center px-4 py-3 rounded-xl transition-all duration-200 
                      ${
                        isActiveItem
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }
                    `}
                  >
                    <div
                      className={`
                      flex-shrink-0 w-5 h-5 mr-4
                      ${isActiveItem ? "text-white" : "text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300"}
                      ${isSidebarCollapsed ? "mr-0" : "mr-4"}
                    `}
                    >
                      {item.icon}
                    </div>

                    {!isSidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="font-medium truncate"
                      >
                        {item.label}
                      </motion.span>
                    )}

                    {isActiveItem && !isSidebarCollapsed && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="ml-auto w-2 h-2 bg-white rounded-full"
                      />
                    )}
                  </Link>

                  {isSidebarCollapsed && (
                    <div className="absolute left-16 ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                      {item.label}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {!isSidebarCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-between"
            >
              <Link
                to="/client"
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Settings className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Paramètres</span>
              </Link>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Main Content */}
      <div
        className={`
          flex-1 transition-all duration-300 ease-in-out
          ${isMobile ? "ml-0" : isSidebarCollapsed ? "ml-0" : "ml-0"}
          ${isMobile ? "pt-20" : "pt-0"}
        `}
      >
        <div className={`h-full overflow-auto ${styles.customScrollbar}`}>{children}</div>
      </div>
    </div>
  );
};

export default CommonLayout;
